import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import {
  api,
  ApiError,
  type CategoryNode,
  type ProductFilter,
  type ProductRow,
} from '../api/client'
import { useAuth } from '../auth/AuthProvider'
import { Button, EmptyState, Input, Notice, Select, Spinner } from '../ui/controls'
import { imageUrl } from '../ui/images'

/**
 * Список товаров (п. 4.6).
 *
 * Поиск, фильтрация, сортировка и постраничный вывод выполняются на
 * серверной части: при проектной ёмкости в 100 000 позиций (Н-108)
 * выбирать всё в браузер нельзя.
 */
export default function ProductsPage() {
  const nav = useNavigate()
  const { state } = useAuth()
  const canWrite = state.status === 'authenticated' && state.me.permissions.write.catalog
  const canPrice = state.status === 'authenticated' && state.me.permissions.write.pricing

  const [filter, setFilter] = useState<ProductFilter>({ page: 1, limit: 25, sort: 'name' })
  const [rows, setRows] = useState<ProductRow[] | null>(null)
  const [total, setTotal] = useState(0)
  const [categories, setCategories] = useState<CategoryNode[]>([])
  const [brands, setBrands] = useState<string[]>([])
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [allByFilter, setAllByFilter] = useState(false)
  const [notice, setNotice] = useState<{ kind: 'error' | 'success' | 'warning'; text: string } | null>(null)

  const reload = useCallback(async (f: ProductFilter) => {
    try {
      const res = await api.products(f)
      setRows(res.products ?? [])
      setTotal(res.total)
    } catch (e) {
      setNotice({ kind: 'error', text: e instanceof ApiError ? e.message : 'Не удалось загрузить' })
      setRows([])
    }
  }, [])

  useEffect(() => {
    const t = setTimeout(() => void reload(filter), 200)
    return () => clearTimeout(t)
  }, [filter, reload])

  useEffect(() => {
    void api.categoryTree().then((t) => setCategories(flat(t)))
    void api.productBrands().then(setBrands)
  }, [])

  function patch(p: Partial<ProductFilter>) {
    // Любое изменение условий сбрасывает страницу: иначе можно оказаться
    // на несуществующей странице новой выборки.
    setFilter((f) => ({ ...f, ...p, page: p.page ?? 1 }))
    setSelected(new Set())
    setAllByFilter(false)
  }

  const pages = Math.max(1, Math.ceil(total / (filter.limit ?? 25)))
  const selectionCount = allByFilter ? total : selected.size

  return (
    <section>
      <header className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-ink-900 dark:text-ink-50">Товары</h2>
          <p className="mt-0.5 text-sm text-ink-500">Найдено: {total}</p>
        </div>
        {canWrite && (
          <Button variant="primary" size="sm" onClick={() => nav('/products/new')}>
            + Товар
          </Button>
        )}
      </header>

      {notice && (
        <Notice kind={notice.kind} onClose={() => setNotice(null)}>
          {notice.text}
        </Notice>
      )}

      <Filters
        filter={filter}
        categories={categories}
        brands={brands}
        onChange={patch}
      />

      {selectionCount > 0 && canWrite && (
        <BulkBar
          count={selectionCount}
          canPrice={canPrice}
          categories={categories}
          onRun={async (body) => {
            try {
              const res = await api.bulkProducts(
                allByFilter ? { ...body, all_by_filter: true } : { ...body, ids: [...selected] },
                allByFilter ? filter : undefined,
              )
              setSelected(new Set())
              setAllByFilter(false)
              setNotice({
                kind: res.skipped?.length ? 'warning' : 'success',
                text: res.note
                  ? `${res.status}: ${res.affected}. ${res.note} (${res.skipped?.length})`
                  : `${res.status}: ${res.affected}`,
              })
              await reload(filter)
            } catch (e) {
              setNotice({
                kind: 'error',
                text: e instanceof ApiError ? e.message : 'Операция не выполнена',
              })
            }
          }}
          onCancel={() => {
            setSelected(new Set())
            setAllByFilter(false)
          }}
        />
      )}

      {rows === null ? (
        <Spinner label="Загрузка товаров…" />
      ) : rows.length === 0 ? (
        <EmptyState
          title="Товары не найдены"
          hint="Измените условия отбора либо заведите первый товар. Для товара нужна категория — создайте её в разделе «Категории»."
          action={
            canWrite ? (
              <Button variant="primary" onClick={() => nav('/products/new')}>
                Создать товар
              </Button>
            ) : undefined
          }
        />
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg border border-ink-200 dark:border-ink-800">
            <table className="w-full min-w-[58rem] text-left text-sm">
              <thead className="sticky top-0 border-b border-ink-200 bg-ink-50 text-xs uppercase tracking-wide text-ink-500 dark:border-ink-800 dark:bg-ink-900">
                <tr>
                  <th className="w-10 px-3 py-2.5">
                    <input
                      type="checkbox"
                      aria-label="Выделить страницу"
                      checked={rows.length > 0 && rows.every((r) => selected.has(r.id))}
                      onChange={(e) => {
                        setAllByFilter(false)
                        setSelected(
                          e.target.checked ? new Set(rows.map((r) => r.id)) : new Set(),
                        )
                      }}
                      className="size-4 rounded border-ink-300"
                    />
                  </th>
                  <th className="w-14 px-3 py-2.5 font-medium">Фото</th>
                  <SortHeader field="sku" filter={filter} onChange={patch}>
                    Артикул
                  </SortHeader>
                  <SortHeader field="name" filter={filter} onChange={patch}>
                    Название
                  </SortHeader>
                  <th className="px-3 py-2.5 font-medium">Категория</th>
                  <SortHeader field="price" filter={filter} onChange={patch} align="right">
                    Цена
                  </SortHeader>
                  <SortHeader field="stock" filter={filter} onChange={patch} align="right">
                    Остаток
                  </SortHeader>
                  <th className="px-3 py-2.5 font-medium">Состояние</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-200 dark:divide-ink-800">
                {rows.map((p) => (
                  <tr key={p.id} className="hover:bg-ink-50 dark:hover:bg-ink-900">
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        aria-label={`Выделить ${p.sku}`}
                        checked={selected.has(p.id) || allByFilter}
                        onChange={(e) => {
                          setAllByFilter(false)
                          const next = new Set(selected)
                          e.target.checked ? next.add(p.id) : next.delete(p.id)
                          setSelected(next)
                        }}
                        className="size-4 rounded border-ink-300"
                      />
                    </td>
                    <td className="px-3 py-2">
                      {p.image_key ? (
                        <img
                          src={imageUrl(p.image_key, 'thumb')}
                          alt=""
                          className="size-9 rounded object-cover"
                        />
                      ) : (
                        <span className="flex size-9 items-center justify-center rounded bg-ink-100 text-xs text-ink-400 dark:bg-ink-900">
                          —
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-ink-600 dark:text-ink-400">
                      <button type="button" onClick={() => nav(`/products/${p.id}`)}>
                        {p.sku}
                      </button>
                    </td>
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        onClick={() => nav(`/products/${p.id}`)}
                        className="text-left text-ink-800 hover:text-brand-600 dark:text-ink-200"
                      >
                        {p.name_ru}
                      </button>
                      {p.brand && <div className="text-xs text-ink-400">{p.brand}</div>}
                    </td>
                    <td className="px-3 py-2 text-ink-600 dark:text-ink-400">{p.category_name}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-ink-800 dark:text-ink-200">
                      {p.price.toLocaleString('ru-RU')} ₸
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      <span className={p.stock_total > 0 ? 'text-ink-800 dark:text-ink-200' : 'text-ink-400'}>
                        {p.stock_total}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <StateBadges row={p} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {selected.size > 0 && selected.size === rows.length && total > rows.length && !allByFilter && (
            <button
              type="button"
              onClick={() => setAllByFilter(true)}
              className="mt-2 text-sm text-brand-600 hover:underline"
            >
              Выделить всю выборку — {total} товаров
            </button>
          )}

          <div className="mt-3 flex flex-wrap items-center justify-between gap-3 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-ink-500">На странице:</span>
              {[25, 50, 100].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => patch({ limit: n })}
                  className={`rounded px-2 py-0.5 ${
                    filter.limit === n ? 'bg-brand-50 dark:bg-brand-500/15 text-brand-700 dark:text-brand-300' : 'text-ink-500 hover:text-ink-800 dark:hover:text-ink-100'
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                disabled={(filter.page ?? 1) <= 1}
                onClick={() => patch({ page: (filter.page ?? 1) - 1 })}
              >
                Назад
              </Button>
              <span className="text-ink-500">
                {filter.page ?? 1} из {pages}
              </span>
              <Button
                size="sm"
                disabled={(filter.page ?? 1) >= pages}
                onClick={() => patch({ page: (filter.page ?? 1) + 1 })}
              >
                Вперёд
              </Button>
            </div>
          </div>
        </>
      )}
    </section>
  )
}

// --------------------------------------------------------------------------

function SortHeader({
  field,
  filter,
  onChange,
  align,
  children,
}: {
  field: string
  filter: ProductFilter
  onChange: (p: Partial<ProductFilter>) => void
  align?: 'right'
  children: React.ReactNode
}) {
  const active = filter.sort === field
  return (
    <th className={`px-3 py-2.5 font-medium ${align === 'right' ? 'text-right' : ''}`}>
      <button
        type="button"
        onClick={() => onChange({ sort: field, desc: active ? !filter.desc : false })}
        className={`inline-flex items-center gap-1 ${active ? 'text-brand-700 dark:text-brand-300' : ''}`}
      >
        {children}
        {active && <span aria-hidden>{filter.desc ? '▾' : '▴'}</span>}
      </button>
    </th>
  )
}

function StateBadges({ row }: { row: ProductRow }) {
  return (
    <div className="flex flex-wrap gap-1">
      {row.is_published ? (
        <span className="rounded-full bg-success-100 dark:bg-success-700/25 px-2 py-0.5 text-xs text-success-700 dark:text-success-100">
          в продаже
        </span>
      ) : (
        <span className="rounded-full bg-ink-100 px-2 py-0.5 text-xs text-ink-600 dark:bg-ink-800 dark:text-ink-400">
          черновик
        </span>
      )}
      {row.missing_required > 0 && (
        <span
          className="rounded-full bg-warning-100 dark:bg-warning-700/25 px-2 py-0.5 text-xs text-warning-700 dark:text-warning-100"
          title="Незаполненные обязательные атрибуты"
        >
          атрибуты: {row.missing_required}
        </span>
      )}
      {row.image_count === 0 && (
        <span className="rounded-full bg-warning-100 dark:bg-warning-700/25 px-2 py-0.5 text-xs text-warning-700 dark:text-warning-100">
          нет фото
        </span>
      )}
    </div>
  )
}

function Filters({
  filter,
  categories,
  brands,
  onChange,
}: {
  filter: ProductFilter
  categories: CategoryNode[]
  brands: string[]
  onChange: (p: Partial<ProductFilter>) => void
}) {
  return (
    <div className="mb-4 flex flex-wrap items-end gap-2">
      <div className="min-w-56 flex-1">
        <Input
          type="search"
          value={filter.q ?? ''}
          onChange={(e) => onChange({ q: e.target.value })}
          placeholder="Артикул, название, штрихкод или бренд"
          aria-label="Поиск товаров"
        />
      </div>

      <LabeledSelect
        label="Категория"
        value={filter.category_id ?? ''}
        onChange={(v) => onChange({ category_id: v === '' ? null : Number(v) })}
        options={[
          { value: '', label: 'Все' },
          ...categories.map((c) => ({
            value: c.id,
            label: `${' '.repeat((c.depth - 1) * 2)}${c.name_ru}`,
          })),
        ]}
      />

      <LabeledSelect
        label="Бренд"
        value={filter.brand ?? ''}
        onChange={(v) => onChange({ brand: v === '' ? null : String(v) })}
        options={[{ value: '', label: 'Все' }, ...brands.map((b) => ({ value: b, label: b }))]}
      />

      <TriSelect
        label="Публикация"
        value={filter.published}
        yes="в продаже"
        no="черновики"
        onChange={(v) => onChange({ published: v })}
      />
      <TriSelect
        label="Наличие"
        value={filter.in_stock}
        yes="в наличии"
        no="нет в наличии"
        onChange={(v) => onChange({ in_stock: v })}
      />
      <TriSelect
        label="Фото"
        value={filter.has_images}
        yes="есть"
        no="нет"
        onChange={(v) => onChange({ has_images: v })}
      />
      <TriSelect
        label="Атрибуты"
        value={filter.attributes_complete}
        yes="заполнены"
        no="с пропусками"
        onChange={(v) => onChange({ attributes_complete: v })}
      />
    </div>
  )
}

/**
 * Список выбора с подписью — обёртка над общим Select.
 *
 * Метрики берутся из общего органа управления, здесь только подпись
 * и приведение значения.
 */
function LabeledSelect({
  label,
  value,
  options,
  onChange,
}: {
  label: string
  value: string | number
  options: { value: string | number; label: string }[]
  onChange: (v: string | number) => void
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs text-ink-500">{label}</span>
      <Select value={value} onChange={(e) => onChange(e.target.value)}
        className="rounded-md border border-ink-300 bg-white px-2.5 py-2 text-sm text-ink-900 dark:border-ink-700 dark:bg-ink-900 dark:text-ink-50"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </Select>
    </label>
  )
}

function TriSelect({
  label,
  value,
  yes,
  no,
  onChange,
}: {
  label: string
  value: boolean | null | undefined
  yes: string
  no: string
  onChange: (v: boolean | null) => void
}) {
  return (
    <LabeledSelect
      label={label}
      value={value === undefined || value === null ? '' : String(value)}
      onChange={(v) => onChange(v === '' ? null : v === 'true')}
      options={[
        { value: '', label: 'Все' },
        { value: 'true', label: yes },
        { value: 'false', label: no },
      ]}
    />
  )
}

/** Групповые операции над выборкой (А-507) с подтверждением (А-508). */
function BulkBar({
  count,
  canPrice,
  categories,
  onRun,
  onCancel,
}: {
  count: number
  canPrice: boolean
  categories: CategoryNode[]
  onRun: (body: Record<string, unknown>) => Promise<void>
  onCancel: () => void
}) {
  const [op, setOp] = useState('')
  const [categoryId, setCategoryId] = useState<number | ''>('')
  const [percent, setPercent] = useState('')
  const [confirm, setConfirm] = useState<Record<string, unknown> | null>(null)

  return (
    <div className="mb-4 rounded-lg border border-brand-500/30 bg-brand-50 dark:bg-brand-500/15 p-3">
      <div className="flex flex-wrap items-end gap-2">
        <span className="py-2 text-sm font-medium text-brand-700 dark:text-brand-300">Выбрано: {count}</span>

        <LabeledSelect
          label="Операция"
          value={op}
          onChange={(v) => setOp(String(v))}
          options={[
            { value: '', label: '— выберите —' },
            { value: 'set_category', label: 'Сменить категорию' },
            ...(canPrice
              ? [
                  { value: 'change_price', label: 'Изменить цену на процент' },
                  { value: 'publish', label: 'Опубликовать' },
                  { value: 'unpublish', label: 'Снять с публикации' },
                ]
              : []),
          ]}
        />

        {op === 'set_category' && (
          <LabeledSelect
            label="Категория"
            value={categoryId}
            onChange={(v) => setCategoryId(v === '' ? '' : Number(v))}
            options={[
              { value: '', label: '— выберите —' },
              ...categories.map((c) => ({
                value: c.id,
                label: `${' '.repeat((c.depth - 1) * 2)}${c.name_ru}`,
              })),
            ]}
          />
        )}

        {op === 'change_price' && (
          <label className="block">
            <span className="mb-1 block text-xs text-ink-500">Процент</span>
            <Input
              type="number"
              value={percent}
              onChange={(e) => setPercent(e.target.value)}
              placeholder="например, -10"
              className="w-32"
            />
          </label>
        )}

        <Button
          variant="primary"
          size="sm"
          disabled={
            op === '' ||
            (op === 'set_category' && categoryId === '') ||
            (op === 'change_price' && percent === '')
          }
          onClick={() => {
            const body: Record<string, unknown> = { operation: op }
            if (op === 'set_category') {
              body.category_id = categoryId
              body.keep_values = true
            }
            if (op === 'change_price') body.percent = Number(percent)
            setConfirm(body)
          }}
        >
          Применить
        </Button>
        <Button size="sm" onClick={onCancel}>
          Снять выделение
        </Button>
      </div>

      {confirm && (
        <div className="mt-3 rounded-md border border-warning-500/40 bg-warning-100 dark:bg-warning-700/25 px-3 py-2 text-sm text-warning-700 dark:text-warning-100">
          <p>Операция затронет {count} товаров. Продолжить?</p>
          <div className="mt-2 flex gap-2">
            <Button
              size="sm"
              variant="primary"
              onClick={async () => {
                const body = confirm
                setConfirm(null)
                await onRun(body)
              }}
            >
              Да, применить
            </Button>
            <Button size="sm" onClick={() => setConfirm(null)}>
              Отмена
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

function flat(nodes: CategoryNode[]): CategoryNode[] {
  const out: CategoryNode[] = []
  for (const n of nodes) {
    out.push(n)
    if (n.children?.length) out.push(...flat(n.children))
  }
  return out
}
